import React, { useState, useEffect, useCallback, useRef } from "react";
import "./lightbox.css";

// Custom hook to manage lightbox functionality
export const useLightbox = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [image, setImage] = useState(null);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [showControls, setShowControls] = useState(true);

  const openLightbox = (imageSrc) => {
    setImage(imageSrc);
    setIsOpen(true);
    setScale(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
    setShowControls(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setIsOpen(false);
    setScale(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
    document.body.style.overflow = "auto";
  };

  const zoomIn = () => {
    setScale((prevScale) => Math.min(prevScale + 0.2, 5));
  };

  const zoomOut = () => {
    setScale((prevScale) => Math.max(prevScale - 0.2, 0.5));
  };

  const resetZoom = () => {
    setScale(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  };

  const rotateClockwise = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const rotateCounterClockwise = () => {
    setRotation((prev) => (prev - 90) % 360);
  };

  const toggleControls = () => {
    setShowControls((prev) => !prev);
  };

  // Function to handle arrow key panning
  const panImage = (direction, step = 20) => {
    setPosition((prev) => {
      switch (direction) {
        case "up":
          return { ...prev, y: prev.y + step };
        case "down":
          return { ...prev, y: prev.y - step };
        case "left":
          return { ...prev, x: prev.x + step };
        case "right":
          return { ...prev, x: prev.x - step };
        default:
          return prev;
      }
    });
  };

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      switch (e.key) {
        case "Escape":
          closeLightbox();
          break;
        case "+":
        case "=": // Adding = as it's the same key as + without shift
          zoomIn();
          break;
        case "-":
          zoomOut();
          break;
        case "0":
          resetZoom();
          break;
        case "r":
          rotateClockwise();
          break;
        case "l":
          rotateCounterClockwise();
          break;
        case "h":
          toggleControls();
          break;
        case "ArrowUp":
          e.preventDefault();
          panImage("up");
          break;
        case "ArrowDown":
          e.preventDefault();
          panImage("down");
          break;
        case "ArrowLeft":
          e.preventDefault();
          panImage("left");
          break;
        case "ArrowRight":
          e.preventDefault();
          panImage("right");
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  return {
    isOpen,
    image,
    scale,
    rotation,
    position,
    showControls,
    setPosition,
    openLightbox,
    closeLightbox,
    zoomIn,
    zoomOut,
    resetZoom,
    rotateClockwise,
    rotateCounterClockwise,
    toggleControls,
    panImage,
  };
};

// Lightbox component
export const Lightbox = ({
  image,
  isOpen,
  onClose,
  scale,
  rotation,
  position,
  showControls,
  setPosition,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onRotateClockwise,
  onRotateCounterClockwise,
  onToggleControls,
  panImage,
}) => {
  const lightboxRef = useRef(null);
  const imageRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [showInfo, setShowInfo] = useState(false);
  // Navigation step size - adjust for faster/slower movement
  const navStepSize = 150;

  // Handle image load to get dimensions
  const handleImageLoad = (e) => {
    setImageDimensions({
      width: e.target.naturalWidth,
      height: e.target.naturalHeight,
    });
    setImageLoaded(true);
  };

  // Handle mouse wheel zoom
  const handleWheel = useCallback(
    (e) => {
      if (!isOpen) return;

      e.preventDefault();
      if (e.deltaY < 0) {
        onZoomIn();
      } else {
        onZoomOut();
      }
    },
    [isOpen, onZoomIn, onZoomOut]
  );

  const toggleInfoPanel = () => {
    setShowInfo((prev) => !prev);
  };

  // Handle navigation button clicks
  const handleNavClick = (direction) => {
    panImage(direction, navStepSize);
  };

  useEffect(() => {
    const lightbox = lightboxRef.current;
    if (lightbox) {
      lightbox.addEventListener("wheel", handleWheel, { passive: false });
      return () => lightbox.removeEventListener("wheel", handleWheel);
    }
  }, [handleWheel]);

  // Handle keyboard events for navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      switch (e.key) {
        case "Escape":
          onClose();
          break;
        case "+":
        case "=":
          onZoomIn();
          break;
        case "-":
          onZoomOut();
          break;
        case "0":
          onResetZoom();
          break;
        case "r":
          onRotateClockwise();
          break;
        case "l":
          onRotateCounterClockwise();
          break;
        case "h":
          onToggleControls();
          break;
        case "i":
          toggleInfoPanel();
          break;
        case "ArrowUp":
          e.preventDefault();
          panImage("up", navStepSize);
          break;
        case "ArrowDown":
          e.preventDefault();
          panImage("down", navStepSize);
          break;
        case "ArrowLeft":
          e.preventDefault();
          panImage("left", navStepSize);
          break;
        case "ArrowRight":
          e.preventDefault();
          panImage("right", navStepSize);
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    isOpen,
    onClose,
    onZoomIn,
    onZoomOut,
    onResetZoom,
    onRotateClockwise,
    onRotateCounterClockwise,
    onToggleControls,
    panImage,
    navStepSize,
  ]);

  if (!isOpen) return null;

  // Extract filename from URL
  const getFileName = (url) => {
    if (!url) return "Unknown";
    return url.split("/").pop().split("?")[0];
  };

  return (
    <div className="lightbox-overlay" ref={lightboxRef}>
      {showControls ? (
        <div className="lightbox-controls-bar">
          <div className="lightbox-title">{getFileName(image)}</div>

          <div className="lightbox-controls">
            <button
              className="lightbox-button"
              onClick={toggleInfoPanel}
              title="Toggle Info Panel (I key)"
            >
              <img
                src="/assets/info.svg"
                style={{ width: 20, height: 20 }}
                alt=""
              />
            </button>

            <div className="control-group zoom-controls">
              <button
                className="lightbox-button"
                onClick={onZoomOut}
                title="Zoom Out (- key)"
              >
                <img
                  src="/assets/zoom-out.svg"
                  style={{ width: 20, height: 20 }}
                  alt=""
                />
              </button>
              <span className="zoom-level">{Math.round(scale * 100)}%</span>
              <button
                className="lightbox-button"
                onClick={onZoomIn}
                title="Zoom In (+ key)"
              >
                <img
                  src="/assets/zoom-in.svg"
                  style={{ width: 20, height: 20 }}
                  alt=""
                />
              </button>
            </div>

            <div className="control-group">
              <button
                className="lightbox-button"
                onClick={onRotateCounterClockwise}
                title="Rotate Left (L key)"
              >
                <img
                  src="/assets/left-rotate.svg"
                  style={{ width: 35, height: 35 }}
                  alt=""
                />
              </button>
              <button
                className="lightbox-button"
                onClick={onRotateClockwise}
                title="Rotate Right (R key)"
              >
                <img
                  src="/assets/right-rotate.svg"
                  style={{ width: 35, height: 35 }}
                  alt=""
                />
              </button>
            </div>

            <button
              className="lightbox-button reset-button"
              onClick={onResetZoom}
              title="Reset View (0 key)"
            >
              Reset
            </button>

            <button
              className="lightbox-button"
              onClick={onToggleControls}
              title="Hide Controls (H key)"
            >
              <img
                src="/assets/hide.svg"
                style={{ width: 20, height: 20 }}
                alt=""
              />
            </button>

            <button
              className="lightbox-close"
              onClick={onClose}
              title="Close (ESC key)"
            >
              <img
                src="/assets/close.svg"
                style={{ width: 20, height: 20 }}
                alt=""
              />
            </button>
          </div>
        </div>
      ) : (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "end",
            alignItems: "end",
          }}
        >
          {" "}
          <button
            className="lightbox-button m-2"
            onClick={onToggleControls}
            title="Show Controls (H key)"
          >
            <img
              src="/assets/show.svg"
              style={{ width: 20, height: 20 }}
              alt=""
            />
          </button>
        </div>
      )}

      <div
        className="lightbox-container"
        onClick={(e) => {
          // Only toggle controls if clicking the background
          if (e.target === e.currentTarget) {
            onToggleControls();
          }
        }}
      >
        <div className="lightbox-image-container">
          <div className="lightbox-image-wrapper">
            <img
              ref={imageRef}
              src={image}
              alt="Lightbox preview"
              className="lightbox-image"
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale}) rotate(${rotation}deg)`,
              }}
              onLoad={handleImageLoad}
              draggable="false"
            />

            {/* Navigation controls - only show when zoomed in */}
            {scale > 1 && (
              <div className="nav-controls">
                <div className="nav-controls-inner">
                  <button
                    className="nav-button nav-up"
                    onClick={() => handleNavClick("up")}
                    title="Move Up"
                  >
                    <svg viewBox="0 0 24 24" width="24" height="24">
                      <path
                        fill="currentColor"
                        d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z"
                      />
                    </svg>
                  </button>
                  <div className="nav-horizontal">
                    <button
                      className="nav-button nav-left"
                      onClick={() => handleNavClick("left")}
                      title="Move Left"
                    >
                      <svg viewBox="0 0 24 24" width="24" height="24">
                        <path
                          fill="currentColor"
                          d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"
                        />
                      </svg>
                    </button>
                    <button
                      className="nav-button nav-down"
                      onClick={() => handleNavClick("down")}
                      title="Move Down"
                    >
                      <svg viewBox="0 0 24 24" width="24" height="24">
                        <path
                          fill="currentColor"
                          d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z"
                        />
                      </svg>
                    </button>
                    <button
                      className="nav-button nav-right"
                      onClick={() => handleNavClick("right")}
                      title="Move Right"
                    >
                      <svg viewBox="0 0 24 24" width="24" height="24">
                        <path
                          fill="currentColor"
                          d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {showInfo && imageLoaded && (
          <div className="lightbox-info-panel">
            <h3>Image Analysis</h3>
            <div className="lightbox-info-grid">
              <div className="lightbox-info-item">
                <span className="info-label">Dimensions:</span>
                <span className="info-value">
                  {imageDimensions.width} × {imageDimensions.height}px
                </span>
              </div>
              <div className="lightbox-info-item">
                <span className="info-label">Aspect Ratio:</span>
                <span className="info-value">
                  {(imageDimensions.width / imageDimensions.height).toFixed(2)}
                </span>
              </div>
              <div className="lightbox-info-item">
                <span className="info-label">Zoom:</span>
                <span className="info-value">{Math.round(scale * 100)}%</span>
              </div>
              <div className="lightbox-info-item">
                <span className="info-label">Rotation:</span>
                <span className="info-value">{rotation}°</span>
              </div>
              <div className="lightbox-info-item">
                <span className="info-label">File:</span>
                <span className="info-value">{getFileName(image)}</span>
              </div>
            </div>
            <div className="lightbox-keyboard-shortcuts">
              <h4>Keyboard Shortcuts</h4>
              <div className="shortcuts-grid">
                <div>+/= : Zoom In</div>
                <div>- : Zoom Out</div>
                <div>0 : Reset View</div>
                <div>r : Rotate Right</div>
                <div>l : Rotate Left</div>
                <div>h : Toggle Controls</div>
                <div>ESC : Close</div>
                <div>↑↓←→ : Pan Image</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {scale > 1 && !showInfo && (
        <div className="nav-hint">
          Use navigation buttons or arrow keys to move the image
        </div>
      )}
    </div>
  );
};

// Example usage component
export const ImageWithLightbox = ({
  src,
  alt = "Image",
  width = "auto",
  height = "auto",
}) => {
  const {
    isOpen,
    image,
    scale,
    rotation,
    position,
    showControls,
    setPosition,
    openLightbox,
    closeLightbox,
    zoomIn,
    zoomOut,
    resetZoom,
    rotateClockwise,
    rotateCounterClockwise,
    toggleControls,
    panImage,
  } = useLightbox();

  return (
    <>
      <img
        src={src}
        alt={alt}
        onClick={() => openLightbox(src)}
        className="lightbox-thumbnail"
        style={{ width, height, cursor: "pointer" }}
      />
      <Lightbox
        image={image}
        isOpen={isOpen}
        onClose={closeLightbox}
        scale={scale}
        rotation={rotation}
        position={position}
        showControls={showControls}
        setPosition={setPosition}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onResetZoom={resetZoom}
        onRotateClockwise={rotateClockwise}
        onRotateCounterClockwise={rotateCounterClockwise}
        onToggleControls={toggleControls}
        panImage={panImage}
      />
    </>
  );
};

export default ImageWithLightbox;
